/**
 * CFB subdivision (FBS / FCS) classification — presentation only (#653).
 *
 * WHY A REPO-OWNED TABLE. Producer-owned classification was preferred and is
 * NOT available: a full prod-hydra market pull on 2026-08-20 (5,018 markets,
 * 3,703 of them CFB across 184 games) showed CFB frames carry no conference,
 * division, subdivision, or league field — only `sport:"CFB"`, team codes, and
 * school names. The exact upstream ask that would retire this table is written
 * up in `docs/cfb-subdivision-taxonomy.md`; when hydra attaches a subdivision
 * to CFB frames, `subdivisionForCode` below is the one seam to change.
 *
 * WHAT IT IS NOT. This never touches sport authority. Every CFB row keeps
 * `sport === 'cfb'` — permissions, limits, credentials, and order routing see
 * CFB and nothing else. The buckets exist for dashboard filtering only.
 *
 * SAFE FOR UNKNOWN TEAMS. The table holds only codes VERIFIED against the live
 * slate (see `__fixtures__/cfbSlate.2026-08-20.json`); nothing is inferred from
 * a school's name. A code we have never seen classifies as unclassified, which
 * keeps its games on the default CFB board and surfaces them in the Other
 * bucket — the onboarding queue, not a silent guess. Coverage does drift: the
 * 8 Ivy League programs and the UTRGV-at-UTSA game arrived on the slate
 * between two pulls hours apart during this issue's own build.
 */

/** The two NCAA Division I football subdivisions. A row can carry both. */
export type CfbSubdivision = 'fbs' | 'fcs';

/** A dashboard filter bucket. 'other' is "we could not classify this game" —
 *  never "we chose to hide it". */
export type CfbBucket = CfbSubdivision | 'other';

/** The minimal row surface the classifier needs — structurally satisfied by
 *  GameRow and by focusedDashboard's FocusableGameRow without importing
 *  either full shape. Codes are optional because a row can reach the board
 *  before hydra has resolved canonical identity — on the captured slate the
 *  UTRGV-at-UTSA game had none, and its codes came from the ticker parser.
 *  An unknown display pair stays unclassified unless the existing game
 *  identity supplies two verified CFB codes; no execution alias is created. */
export interface CfbClassifiableRow {
  sport: string;
  awayCode?: string;
  homeCode?: string;
  /** Existing game grouping identity, not a trading alias. When display
   *  codes are venue slug spellings, its verified pair can still classify
   *  the game without assigning either code to an execution side. */
  key?: string;
}

interface CfbTeam {
  /** School name exactly as Kalshi spells it on the frame (`subtitle`) — the
   *  audit trail for a code, and what a reviewer checks an entry against. */
  school: string;
  subdivision: CfbSubdivision;
}

/**
 * Canonical Kalshi team code → school + subdivision, for every code observed
 * on the live CFB slate. Membership is NCAA Division I football subdivision as
 * of the 2026 season; a school's conference is deliberately not stored (it
 * churns yearly and no product rule reads it).
 *
 * A school in a multi-year transition is recorded at the subdivision it PLAYS
 * in now, not the one it is eligible for postseason in. North Dakota St. and
 * Sacramento St. moved up for 2026 and are FBS here even though neither is
 * bowl/CFP-eligible until 2028; New Haven and Mercyhurst are FCS on the same
 * rule from the other direction. A trader filtering the board wants the games,
 * not the eligibility.
 *
 * Codes are CFB-scoped: Kalshi reuses short codes across sports, so this table
 * must only ever be consulted for rows whose sport is 'cfb'.
 */
const CFB_TEAMS: Readonly<Record<string, CfbTeam>> = {
  AAMU: { school: 'Alabama A&M', subdivision: 'fcs' },
  AC: { school: 'Abilene Christian', subdivision: 'fcs' },
  AFA: { school: 'Air Force', subdivision: 'fbs' },
  AKR: { school: 'Akron', subdivision: 'fbs' },
  ALA: { school: 'Alabama', subdivision: 'fbs' },
  ALBY: { school: 'University at Albany', subdivision: 'fcs' },
  ALCN: { school: 'Alcorn St.', subdivision: 'fcs' },
  ALST: { school: 'Alabama St.', subdivision: 'fcs' },
  APP: { school: 'Appalachian St.', subdivision: 'fbs' },
  ARIZ: { school: 'Arizona', subdivision: 'fbs' },
  ARK: { school: 'Arkansas', subdivision: 'fbs' },
  ARMY: { school: 'Army', subdivision: 'fbs' },
  ARPB: { school: 'Arkansas-Pine Bluff', subdivision: 'fcs' },
  ARST: { school: 'Arkansas St.', subdivision: 'fbs' },
  ASU: { school: 'Arizona St.', subdivision: 'fbs' },
  AUB: { school: 'Auburn', subdivision: 'fbs' },
  BALL: { school: 'Ball St.', subdivision: 'fbs' },
  BAY: { school: 'Baylor', subdivision: 'fbs' },
  BC: { school: 'Boston College', subdivision: 'fbs' },
  BGSU: { school: 'Bowling Green', subdivision: 'fbs' },
  BRWN: { school: 'Brown', subdivision: 'fcs' },
  BRY: { school: 'Bryant', subdivision: 'fcs' },
  BSU: { school: 'Boise St.', subdivision: 'fbs' },
  BUCK: { school: 'Bucknell', subdivision: 'fcs' },
  BUFF: { school: 'Buffalo', subdivision: 'fbs' },
  BUT: { school: 'Butler', subdivision: 'fcs' },
  BYU: { school: 'BYU', subdivision: 'fbs' },
  CAL: { school: 'California', subdivision: 'fbs' },
  CAMP: { school: 'Campbell', subdivision: 'fcs' },
  CARK: { school: 'Central Arkansas', subdivision: 'fcs' },
  CCAR: { school: 'Coastal Carolina', subdivision: 'fbs' },
  CCSU: { school: 'Central Connecticut St.', subdivision: 'fcs' },
  CHAR: { school: 'Charlotte', subdivision: 'fbs' },
  CHAT: { school: 'Chattanooga', subdivision: 'fcs' },
  CHSO: { school: 'Charleston Southern', subdivision: 'fcs' },
  CIN: { school: 'Cincinnati', subdivision: 'fbs' },
  CIT: { school: 'The Citadel', subdivision: 'fcs' },
  CLEM: { school: 'Clemson', subdivision: 'fbs' },
  CLMB: { school: 'Columbia', subdivision: 'fcs' },
  CMU: { school: 'Central Michigan', subdivision: 'fbs' },
  COLG: { school: 'Colgate', subdivision: 'fcs' },
  COLO: { school: 'Colorado', subdivision: 'fbs' },
  CONN: { school: 'UConn', subdivision: 'fbs' },
  COOK: { school: 'Bethune-Cookman', subdivision: 'fcs' },
  COR: { school: 'Cornell', subdivision: 'fcs' },
  CP: { school: 'Cal Poly', subdivision: 'fcs' },
  CSU: { school: 'Colorado St.', subdivision: 'fbs' },
  DART: { school: 'Dartmouth', subdivision: 'fcs' },
  DAV: { school: 'Davidson', subdivision: 'fcs' },
  DEL: { school: 'Delaware', subdivision: 'fbs' },
  DRKE: { school: 'Drake', subdivision: 'fcs' },
  DSU: { school: 'Delaware St.', subdivision: 'fcs' },
  DUKE: { school: 'Duke', subdivision: 'fbs' },
  DUQ: { school: 'Duquesne', subdivision: 'fcs' },
  ECU: { school: 'East Carolina', subdivision: 'fbs' },
  EIU: { school: 'Eastern Illinois', subdivision: 'fcs' },
  EKY: { school: 'Eastern Kentucky', subdivision: 'fcs' },
  ELON: { school: 'Elon', subdivision: 'fcs' },
  EMU: { school: 'Eastern Michigan', subdivision: 'fbs' },
  ETAM: { school: 'East Texas A&M', subdivision: 'fcs' },
  ETSU: { school: 'East Tennessee St.', subdivision: 'fcs' },
  EWU: { school: 'Eastern Washington', subdivision: 'fcs' },
  FAMU: { school: 'Florida A&M', subdivision: 'fcs' },
  FAU: { school: 'Florida Atlantic', subdivision: 'fbs' },
  FIU: { school: 'Florida International', subdivision: 'fbs' },
  FLA: { school: 'Florida', subdivision: 'fbs' },
  FOR: { school: 'Fordham', subdivision: 'fcs' },
  FRES: { school: 'Fresno St.', subdivision: 'fbs' },
  FSU: { school: 'Florida St.', subdivision: 'fbs' },
  FUR: { school: 'Furman', subdivision: 'fcs' },
  GASO: { school: 'Georgia Southern', subdivision: 'fbs' },
  GAST: { school: 'Georgia St.', subdivision: 'fbs' },
  GT: { school: 'Georgia Tech', subdivision: 'fbs' },
  GTWN: { school: 'Georgetown', subdivision: 'fcs' },
  HAMP: { school: 'Hampton', subdivision: 'fcs' },
  HARV: { school: 'Harvard', subdivision: 'fcs' },
  HAW: { school: 'Hawai\'i', subdivision: 'fbs' },
  HC: { school: 'Holy Cross', subdivision: 'fcs' },
  HCU: { school: 'Houston Christian', subdivision: 'fcs' },
  HOU: { school: 'Houston', subdivision: 'fbs' },
  HOW: { school: 'Howard', subdivision: 'fcs' },
  IDHO: { school: 'Idaho', subdivision: 'fcs' },
  IDST: { school: 'Idaho St.', subdivision: 'fcs' },
  ILL: { school: 'Illinois', subdivision: 'fbs' },
  ILST: { school: 'Illinois St.', subdivision: 'fcs' },
  IND: { school: 'Indiana', subdivision: 'fbs' },
  INST: { school: 'Indiana St.', subdivision: 'fcs' },
  IOWA: { school: 'Iowa', subdivision: 'fbs' },
  ISU: { school: 'Iowa St.', subdivision: 'fbs' },
  JKST: { school: 'Jackson St.', subdivision: 'fcs' },
  JMU: { school: 'James Madison', subdivision: 'fbs' },
  JVST: { school: 'Jacksonville St.', subdivision: 'fbs' },
  KENN: { school: 'Kennesaw St.', subdivision: 'fbs' },
  KENT: { school: 'Kent St.', subdivision: 'fbs' },
  KSU: { school: 'Kansas St.', subdivision: 'fbs' },
  KU: { school: 'Kansas', subdivision: 'fbs' },
  LAF: { school: 'Lafayette', subdivision: 'fcs' },
  LAM: { school: 'Lamar', subdivision: 'fcs' },
  LEH: { school: 'Lehigh', subdivision: 'fcs' },
  LIB: { school: 'Liberty', subdivision: 'fbs' },
  LINW: { school: 'Lindenwood', subdivision: 'fcs' },
  LIU: { school: 'LIU', subdivision: 'fcs' },
  LOU: { school: 'Louisville', subdivision: 'fbs' },
  LSU: { school: 'LSU', subdivision: 'fbs' },
  LT: { school: 'Louisiana Tech', subdivision: 'fbs' },
  MASS: { school: 'UMass', subdivision: 'fbs' },
  MCNS: { school: 'McNeese', subdivision: 'fcs' },
  MD: { school: 'Maryland', subdivision: 'fbs' },
  ME: { school: 'Maine', subdivision: 'fcs' },
  MEM: { school: 'Memphis', subdivision: 'fbs' },
  MER: { school: 'Mercer', subdivision: 'fcs' },
  MHU: { school: 'Mercyhurst', subdivision: 'fcs' },
  MIA: { school: 'Miami (FL)', subdivision: 'fbs' },
  MICH: { school: 'Michigan', subdivision: 'fbs' },
  MINN: { school: 'Minnesota', subdivision: 'fbs' },
  MISS: { school: 'Ole Miss', subdivision: 'fbs' },
  MIZZ: { school: 'Missouri', subdivision: 'fbs' },
  MOH: { school: 'Miami (OH)', subdivision: 'fbs' },
  MONM: { school: 'Monmouth', subdivision: 'fcs' },
  MONT: { school: 'Montana', subdivision: 'fcs' },
  MORG: { school: 'Morgan St.', subdivision: 'fcs' },
  MOSU: { school: 'Missouri St.', subdivision: 'fbs' },
  MRMK: { school: 'Merrimack', subdivision: 'fcs' },
  MRSH: { school: 'Marshall', subdivision: 'fbs' },
  MRST: { school: 'Marist', subdivision: 'fcs' },
  MSST: { school: 'Mississippi St.', subdivision: 'fbs' },
  MSU: { school: 'Michigan St.', subdivision: 'fbs' },
  MTST: { school: 'Montana St.', subdivision: 'fcs' },
  MTU: { school: 'Middle Tennessee', subdivision: 'fbs' },
  MURR: { school: 'Murray St.', subdivision: 'fcs' },
  MVSU: { school: 'Mississippi Valley St.', subdivision: 'fcs' },
  NAU: { school: 'Northern Arizona', subdivision: 'fcs' },
  NAVY: { school: 'Navy', subdivision: 'fbs' },
  NCAT: { school: 'North Carolina A&T', subdivision: 'fcs' },
  NCCU: { school: 'North Carolina Central', subdivision: 'fcs' },
  NCST: { school: 'North Carolina St.', subdivision: 'fbs' },
  ND: { school: 'Notre Dame', subdivision: 'fbs' },
  NDSU: { school: 'North Dakota St.', subdivision: 'fbs' },
  NEB: { school: 'Nebraska', subdivision: 'fbs' },
  NEV: { school: 'Nevada', subdivision: 'fbs' },
  NHC: { school: 'New Haven', subdivision: 'fcs' },
  NICH: { school: 'Nicholls St.', subdivision: 'fcs' },
  NIU: { school: 'Northern Illinois', subdivision: 'fbs' },
  NMSU: { school: 'New Mexico St.', subdivision: 'fbs' },
  NORF: { school: 'Norfolk St.', subdivision: 'fcs' },
  NW: { school: 'Northwestern', subdivision: 'fbs' },
  NWST: { school: 'Northwestern St.', subdivision: 'fcs' },
  ODU: { school: 'Old Dominion', subdivision: 'fbs' },
  OHIO: { school: 'Ohio', subdivision: 'fbs' },
  OKLA: { school: 'Oklahoma', subdivision: 'fbs' },
  OKST: { school: 'Oklahoma St.', subdivision: 'fbs' },
  ORE: { school: 'Oregon', subdivision: 'fbs' },
  ORST: { school: 'Oregon St.', subdivision: 'fbs' },
  OSU: { school: 'Ohio St.', subdivision: 'fbs' },
  PEAY: { school: 'Austin Peay', subdivision: 'fcs' },
  PENN: { school: 'Penn', subdivision: 'fcs' },
  PITT: { school: 'Pittsburgh', subdivision: 'fbs' },
  PRE: { school: 'Presbyterian', subdivision: 'fcs' },
  PRIN: { school: 'Princeton', subdivision: 'fcs' },
  PRST: { school: 'Portland St.', subdivision: 'fcs' },
  PSU: { school: 'Penn St.', subdivision: 'fbs' },
  PUR: { school: 'Purdue', subdivision: 'fbs' },
  PV: { school: 'Prairie View A&M', subdivision: 'fcs' },
  RICE: { school: 'Rice', subdivision: 'fbs' },
  RICH: { school: 'Richmond', subdivision: 'fcs' },
  RMU: { school: 'Robert Morris', subdivision: 'fcs' },
  RUTG: { school: 'Rutgers', subdivision: 'fbs' },
  SAC: { school: 'Sacramento St.', subdivision: 'fbs' },
  SAM: { school: 'Samford', subdivision: 'fcs' },
  SCAR: { school: 'South Carolina', subdivision: 'fbs' },
  SCST: { school: 'South Carolina St.', subdivision: 'fcs' },
  SDAK: { school: 'South Dakota', subdivision: 'fcs' },
  SDST: { school: 'South Dakota St.', subdivision: 'fcs' },
  SDSU: { school: 'San Diego St.', subdivision: 'fbs' },
  SELA: { school: 'Southeastern Louisiana', subdivision: 'fcs' },
  SEMO: { school: 'Southeast Missouri St.', subdivision: 'fcs' },
  SFA: { school: 'Stephen F. Austin', subdivision: 'fcs' },
  SHSU: { school: 'Sam Houston', subdivision: 'fbs' },
  SHU: { school: 'Sacred Heart', subdivision: 'fcs' },
  SIU: { school: 'Southern Illinois', subdivision: 'fcs' },
  SJSU: { school: 'San Jose St.', subdivision: 'fbs' },
  SMU: { school: 'SMU', subdivision: 'fbs' },
  SOU: { school: 'Southern University', subdivision: 'fcs' },
  STAN: { school: 'Stanford', subdivision: 'fbs' },
  STET: { school: 'Stetson', subdivision: 'fcs' },
  STNH: { school: 'Stonehill', subdivision: 'fcs' },
  STON: { school: 'Stony Brook', subdivision: 'fcs' },
  SUU: { school: 'Southern Utah', subdivision: 'fcs' },
  SYR: { school: 'Syracuse', subdivision: 'fbs' },
  TARL: { school: 'Tarleton St.', subdivision: 'fcs' },
  TCU: { school: 'TCU', subdivision: 'fbs' },
  TEM: { school: 'Temple', subdivision: 'fbs' },
  TENN: { school: 'Tennessee', subdivision: 'fbs' },
  TEX: { school: 'Texas', subdivision: 'fbs' },
  TLSA: { school: 'Tulsa', subdivision: 'fbs' },
  TNST: { school: 'Tennessee St.', subdivision: 'fcs' },
  TNTC: { school: 'Tennessee Tech', subdivision: 'fcs' },
  TOL: { school: 'Toledo', subdivision: 'fbs' },
  TOWS: { school: 'Towson', subdivision: 'fcs' },
  TROY: { school: 'Troy', subdivision: 'fbs' },
  TTU: { school: 'Texas Tech', subdivision: 'fbs' },
  TULN: { school: 'Tulane', subdivision: 'fbs' },
  TXAM: { school: 'Texas A&M', subdivision: 'fbs' },
  TXSO: { school: 'Texas Southern', subdivision: 'fcs' },
  TXST: { school: 'Texas St.', subdivision: 'fbs' },
  UAB: { school: 'UAB', subdivision: 'fbs' },
  UCD: { school: 'UC Davis', subdivision: 'fcs' },
  UCF: { school: 'UCF', subdivision: 'fbs' },
  UCLA: { school: 'UCLA', subdivision: 'fbs' },
  UGA: { school: 'Georgia', subdivision: 'fbs' },
  UK: { school: 'Kentucky', subdivision: 'fbs' },
  ULL: { school: 'Louisiana', subdivision: 'fbs' },
  ULM: { school: 'Louisiana-Monroe', subdivision: 'fbs' },
  UNA: { school: 'North Alabama', subdivision: 'fcs' },
  UNC: { school: 'North Carolina', subdivision: 'fbs' },
  UNCO: { school: 'Northern Colorado', subdivision: 'fcs' },
  UND: { school: 'North Dakota', subdivision: 'fcs' },
  UNH: { school: 'New Hampshire', subdivision: 'fcs' },
  UNI: { school: 'Northern Iowa', subdivision: 'fcs' },
  UNLV: { school: 'UNLV', subdivision: 'fbs' },
  UNM: { school: 'New Mexico', subdivision: 'fbs' },
  UNT: { school: 'North Texas', subdivision: 'fbs' },
  URI: { school: 'Rhode Island', subdivision: 'fcs' },
  USA: { school: 'South Alabama', subdivision: 'fbs' },
  USC: { school: 'USC', subdivision: 'fbs' },
  USD: { school: 'San Diego', subdivision: 'fcs' },
  USF: { school: 'South Florida', subdivision: 'fbs' },
  USM: { school: 'Southern Miss', subdivision: 'fbs' },
  UST: { school: 'St. Thomas', subdivision: 'fcs' },
  USU: { school: 'Utah St.', subdivision: 'fbs' },
  UTAH: { school: 'Utah', subdivision: 'fbs' },
  UTEP: { school: 'UTEP', subdivision: 'fbs' },
  UTM: { school: 'Tennessee-Martin', subdivision: 'fcs' },
  UTRGV: { school: 'UT Rio Grande Valley', subdivision: 'fcs' },
  UTSA: { school: 'UTSA', subdivision: 'fbs' },
  UTU: { school: 'Utah Tech', subdivision: 'fcs' },
  UVA: { school: 'Virginia', subdivision: 'fbs' },
  UWGA: { school: 'West Georgia', subdivision: 'fcs' },
  VAN: { school: 'Vanderbilt', subdivision: 'fbs' },
  VILL: { school: 'Villanova', subdivision: 'fcs' },
  VMI: { school: 'VMI', subdivision: 'fcs' },
  VT: { school: 'Virginia Tech', subdivision: 'fbs' },
  WAG: { school: 'Wagner', subdivision: 'fcs' },
  WAKE: { school: 'Wake Forest', subdivision: 'fbs' },
  WASH: { school: 'Washington', subdivision: 'fbs' },
  WCU: { school: 'Western Carolina', subdivision: 'fcs' },
  WEB: { school: 'Weber St.', subdivision: 'fcs' },
  WEBB: { school: 'Gardner-Webb', subdivision: 'fcs' },
  WIS: { school: 'Wisconsin', subdivision: 'fbs' },
  WIU: { school: 'Western Illinois', subdivision: 'fcs' },
  WKU: { school: 'Western Kentucky', subdivision: 'fbs' },
  WM: { school: 'William & Mary', subdivision: 'fcs' },
  WMU: { school: 'Western Michigan', subdivision: 'fbs' },
  WOF: { school: 'Wofford', subdivision: 'fcs' },
  WSU: { school: 'Washington St.', subdivision: 'fbs' },
  WVU: { school: 'West Virginia', subdivision: 'fbs' },
  WYO: { school: 'Wyoming', subdivision: 'fbs' },
  YALE: { school: 'Yale', subdivision: 'fcs' },
  YSU: { school: 'Youngstown St.', subdivision: 'fcs' },
};

/** Subdivision for one canonical team code, or null when the code is not in
 *  the verified table. The single seam a producer-owned subdivision would
 *  replace. */
export function subdivisionForCode(code: string | undefined | null): CfbSubdivision | null {
  if (!code) return null;
  // Normalize rather than trust the caller: hydra's canonical codes are
  // uppercase today, but two of marketStore's row builders pass them through
  // verbatim while their siblings uppercase. A case mismatch would silently
  // drop a known team into Other, so define that failure away here.
  return CFB_TEAMS[code.trim().toUpperCase()]?.subdivision ?? null;
}

/**
 * Every subdivision a CFB game belongs to.
 *
 * A cross-subdivision game (FBS hosting an FCS opponent) returns BOTH — the
 * documented product rule: such a game is discoverable from either bucket
 * rather than being demoted out of one of them. The consequence, which the
 * counts make visible, is that FBS + FCS + Other exceeds the CFB slate size.
 *
 * Empty means unclassified — no complete verified pair in the display codes
 * or existing game key. A non-CFB row is always empty; callers must not read
 * a bucket from another sport's codes.
 */
export function cfbSubdivisionsForRow(row: CfbClassifiableRow): ReadonlySet<CfbSubdivision> {
  const subdivisions = new Set<CfbSubdivision>();
  if (row.sport !== 'cfb') return subdivisions;
  let away = subdivisionForCode(row.awayCode);
  let home = subdivisionForCode(row.homeCode);
  if (!away || !home) {
    // Poly-only lines can display MST/CAH while the producer's grouping key
    // already names MSU/CAL (#1196). Classification needs the unordered pair,
    // not a guessed home/away binding. A parser-generated key with unknown
    // slug codes still fails the same registry check below.
    const parts = row.key?.split('|');
    if (parts && (parts.length === 4 || parts.length === 5)
      && (parts[0] === 'cfb' || parts[0] === 'ncaaf')
      && /^\d{4}-\d{2}-\d{2}$/.test(parts[1])
      && (parts.length === 4 || /^[1-9]\d*$/.test(parts[2]))) {
      const pair = parts.slice(-2).map((code) => code.toUpperCase());
      const first = subdivisionForCode(pair[0]);
      const second = subdivisionForCode(pair[1]);
      // Never let a second identity overrule a known, contradictory team.
      const knownCodes = [row.awayCode, row.homeCode]
        .filter((code): code is string => subdivisionForCode(code) !== null)
        .map((code) => code.trim().toUpperCase());
      if (first && second && pair[0] !== pair[1]
        && knownCodes.every((code) => pair.includes(code))) {
        away = first;
        home = second;
      }
    }
  }
  // One known side is not enough: the unknown side could belong to the other
  // subdivision, so classifying on a single team would put the game in a
  // bucket it may not belong to. Both sides known, or Other.
  if (!away || !home) return subdivisions;
  subdivisions.add(away);
  subdivisions.add(home);
  return subdivisions;
}

/** Whether a CFB row belongs in one dashboard bucket. Non-CFB rows never do. */
export function cfbRowMatchesBucket(row: CfbClassifiableRow, bucket: CfbBucket): boolean {
  const subdivisions = cfbSubdivisionsForRow(row);
  if (bucket === 'other') return row.sport === 'cfb' && subdivisions.size === 0;
  return subdivisions.has(bucket);
}

/** Read-only view of the table, for tests and the onboarding audit. */
export const CFB_TEAM_TABLE: Readonly<Record<string, CfbTeam>> = CFB_TEAMS;
